import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { BookingDTO, Page } from "@shared/types";
import { fmtRange } from "@shared/dates";
import { api, qs } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useScope, useUser } from "@/lib/queries";
import { Dialog } from "../overlays";
import { ChannelChip } from "../ui";
import { MagnifierIcon, SidebarMinimalisticIcon } from "../icons";
import { useRailCollapsed } from "@/lib/nav";
import { destinationsFor } from "./navItems";
import { usePalette } from "@/stores/palette";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/queryKeys";
import { useDebounced } from "@/lib/useDebounced";

/** ⌘K: jump to any screen, or straight to a booking by code, guest or phone. */
export function CommandPalette() {
  const { open, setOpen } = usePalette();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!usePalette.getState().open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  // Mounted only while open, so every ⌘K starts on an empty field with the
  // cursor at the top instead of last night's search.
  if (!open) return null;
  return <Palette onClose={() => setOpen(false)} />;
}

function Palette({ onClose }: { onClose: () => void }) {
  const me = useUser();
  const { scope } = useScope();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, []);

  const term = q.trim();
  // Pages filter instantly; the booking search waits for typing to settle.
  const settled = useDebounced(term);
  const bookings = useQuery({
    queryKey: queryKeys.palette.search(scope, settled),
    queryFn: () =>
      api<Page<BookingDTO>>(
        `${endpoints.bookings.list}${qs({ propertyId: scope, q: settled, limit: 6 })}`,
      ),
    enabled: settled.length >= 2,
    staleTime: 10_000,
    select: (p) => p.items,
  });

  const pages = useMemo(
    () =>
      destinationsFor(me.role).filter(
        (n) => !term || n.label.toLowerCase().includes(term.toLowerCase()),
      ),
    [me.role, term],
  );

  const [, toggleRail] = useRailCollapsed();
  const actions = [
    {
      key: "toggle-rail",
      label: "Toggle side menu",
      hint: "[",
      run: toggleRail,
    },
  ].filter((a) => !term || a.label.toLowerCase().includes(term.toLowerCase()));

  const results: { key: string; run: () => void; node: React.ReactNode }[] = [
    ...pages.map((n) => {
      const Icon = n.icon;
      return {
        key: n.to,
        run: () => void navigate({ to: n.to }),
        node: (
          <>
            <Icon size={18} />
            <span className="flex-1">{n.label}</span>
            <span className="font-mono text-[10px] uppercase tracking-[.1em] text-subtle">
              Go
            </span>
          </>
        ),
      };
    }),
    ...actions.map((a) => ({
      key: a.key,
      run: a.run,
      node: (
        <>
          <SidebarMinimalisticIcon size={18} />
          <span className="flex-1">{a.label}</span>
          <kbd className="rounded-xs bg-sunk px-1.5 py-0.5 font-mono text-[10px] text-subtle">
            {a.hint}
          </kbd>
        </>
      ),
    })),
    ...(bookings.data ?? []).map((b) => ({
      key: b.id,
      run: () =>
        void navigate({
          to: ".",
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            booking: b.code,
          }),
        } as never),
      node: (
        <>
          <ChannelChip channel={b.channel} />
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate font-semibold">
              {b.guest?.name ?? b.blockReason}
            </span>
            <span className="font-mono text-[11px] text-subtle">
              {b.code} · {b.roomName} · {fmtRange(b.checkIn, b.checkOut)}
            </span>
          </span>
        </>
      ),
    })),
  ];

  const go = (i: number) => {
    const r = results[i];
    if (!r) return;
    onClose();
    r.run();
  };

  return (
    <Dialog open onClose={onClose} label="Jump to">
      <div className="flex items-center gap-2 border-b border-line-strong px-4">
        <MagnifierIcon size={20} className="text-subtle" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setCursor(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setCursor((c) => Math.min(results.length - 1, c + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setCursor((c) => Math.max(0, c - 1));
            } else if (e.key === "Enter") go(cursor);
          }}
          placeholder="Screen, guest, phone or booking ID…"
          className="min-h-[56px] flex-1 bg-transparent text-[16px] outline-none placeholder:text-subtle"
        />
      </div>
      <div className="max-h-[50dvh] overflow-y-auto">
        {results.map((r, i) => (
          <button
            key={r.key}
            onMouseEnter={() => setCursor(i)}
            onClick={() => go(i)}
            className={cn(
              "flex min-h-[48px] w-full items-center gap-3 border-b border-line px-4 text-left text-[14.5px]",
              i === cursor ? "bg-sunk" : "bg-surface",
            )}
          >
            {r.node}
          </button>
        ))}
        {term.length >= 2 && bookings.isFetching && (
          <div className="px-4 py-3 font-mono text-[12px] text-subtle">
            Searching bookings…
          </div>
        )}
        {results.length === 0 && !bookings.isFetching && (
          <div className="px-4 py-6 text-center text-[14px] text-muted">
            Nothing matches “{term}”.
          </div>
        )}
      </div>
    </Dialog>
  );
}
